/**
 * One finding, laid out like Linear's issue view: a one-line top bar (back,
 * status, title, one primary action, more, next open), then the title, an
 * Inspector TL;DR (problem, rule, document, next step), the required changes,
 * the proposed fix (every change word by word; its PDF pages open in a
 * dialog, never on load), the activity and the conversation;
 * on the right, a properties panel of click-to-change pickers. Below `xl`
 * the panel folds into a row of chips under the title.
 *
 * The same page serves a regulatory finding and a gap suggestion (control
 * amendment, missing document); only the decision dialogs differ.
 *
 * Keys: j/k previous/next in the queue · Esc back · s status · a assignee ·
 * d due date · c comment.
 *
 * @module components/regulation/finding/FindingDetailPage
 */
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleCheckIcon,
  CircleSlashIcon,
  EllipsisIcon,
  FileTextIcon,
  HistoryIcon,
  LinkIcon,
  MessageSquareIcon,
  RotateCcwIcon,
  ScanSearchIcon,
  WandSparklesIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { fixChecklistProgress, type DocumentChangesView } from "@repo/shared/findingChanges";
import { useFindingChanges } from "../../../state/findingChanges";
import { useFixReview } from "../../../state/findingFixReview";
import { usePolicyActivity, useRecommendations, type OrganizationPerson } from "../../../state/governance";
import { policyVersion, usePolicies } from "../../../state/policies";
import { InspectorIcon } from "../../InspectorIcon";
import { Bone } from "../LoadingSkeletons";
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert";
import { Button } from "../../ui/button";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "../../ui/collapsible";
import { Kbd } from "../../ui/kbd";
import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuShortcut, MenuTrigger } from "../../ui/menu";
import { Tip } from "../../ui/tip";
import { toastManager } from "../../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../ui/tooltip";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";
import { ReopenDialog } from "../policy/ReopenDialog";
import { findingReopenSummary, suggestionReopenSummary } from "../policy/ReopenDialog.logic";
import type { ResolutionEvidence } from "../policy/ResolveRecommendationDialog";
import { SuggestionConversation } from "../policy/SuggestionConversation";
import { FindingFixChecklist } from "./changes/FindingFixChecklist";
import { ProposedFixChanges } from "./changes/ProposedFixChanges";
import { proposalDecisionNote, useApplyProposal } from "./changes/useApplyProposal";
import { findingActivityEvents } from "./findingActivity";
import { findingActivityTimeline } from "./findingActivityTimeline";
import { FindingConversation } from "./FindingConversation";
import { FindingDecisionControl } from "./FindingDecisionControl";
import { decisionSummary, nextStep, SEVERITY_LABEL, type FindingItem } from "./findingItem";
import { BasisIcon, Hint, PersonFace, SeverityGlyph, StatusIcon, WaitingForAdmin, findingStatusLabel, personLabel } from "./findingGlyphs";
import { assigneeOptions, dueOptions, NO_ASSIGNEE, REMOVE_DUE_DATE } from "./findingPickers";
import { copyFindingLink, savedFindingQueueView, useDismissFlow } from "./FindingsQueue";
import { compareFindingItems, dueLabel, filterFindingItems, matchesFindingStage, nextOpenFindingItem, relativeAge } from "./FindingsQueue.logic";
import { findingCanBeRechecked } from "./findingView";
import { Picker, type PickerOption } from "./Picker";
import { proposedFixState } from "./proposedFixState";
import type { FindingActions } from "./useFindingItems";

type PickerKey = "status" | "assignee" | "due";

export interface FindingDetailProps {
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly item: FindingItem;
  readonly items: ReadonlyArray<FindingItem>;
  readonly people: ReadonlyArray<OrganizationPerson>;
  readonly peopleById: ReadonlyMap<string, OrganizationPerson>;
  readonly personName: (id: string) => string | null;
  readonly canManage: boolean;
  readonly actions: FindingActions;
  /** Back to the document's Findings tab instead of the queue. */
  readonly fromPolicy?: string | undefined;
  readonly initialInspector?: boolean;
}

/** Focus the docked composer (c). */
const focusComposer = () =>
  window.requestAnimationFrame(() =>
    window.document.querySelector<HTMLElement>('[data-floating-composer] [data-testid="composer-editor"]')?.focus(),
  );

function PropertyRow({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="-ms-1.5 min-w-0">{children}</div>
    </div>
  );
}

interface PickerProps {
  readonly openPicker: PickerKey | null;
  readonly setOpenPicker: (key: PickerKey | null) => void;
}

function PrimaryProperties({
  item,
  people,
  peopleById,
  canManage,
  actions,
  onResolve,
  onDismiss,
  onReopen,
  layout,
  openPicker,
  setOpenPicker,
}: {
  readonly item: FindingItem;
  readonly people: ReadonlyArray<OrganizationPerson>;
  readonly peopleById: ReadonlyMap<string, OrganizationPerson>;
  readonly canManage: boolean;
  readonly actions: FindingActions;
  readonly onResolve: () => void;
  readonly onDismiss: () => void;
  readonly onReopen: () => void;
  readonly layout: "rows" | "chips";
} & PickerProps) {
  const openFor = (key: PickerKey) => ({ open: openPicker === key, onOpenChange: (open: boolean) => setOpenPicker(open ? key : null) });
  const statusOptions: PickerOption[] =
    item.status === "open"
      ? [
          { value: "open", label: "Open", icon: <StatusIcon status="open" />, checked: true },
          { value: "resolve", label: "Resolve…", icon: <CircleCheckIcon className="size-3.5" /> },
          { value: "dismiss", label: "Dismiss…", icon: <CircleSlashIcon className="size-3.5" /> },
        ]
      : [
          { value: item.status, label: findingStatusLabel(item.status), icon: <StatusIcon status={item.status} />, checked: true },
          { value: "reopen", label: "Reopen…", icon: <RotateCcwIcon className="size-3.5" /> },
        ];
  const plan = (patch: Parameters<FindingActions["plan"]>[1], success: string) =>
    void actions.plan(item, patch).then((error) =>
      toastManager.add(error ? { type: "error", title: "Could not save", description: error } : { type: "success", title: success }),
    );
  const assignee = item.assigneeId ? peopleById.get(item.assigneeId) : undefined;
  const due = item.dueDate ? dueLabel(item.dueDate) : null;
  const chips = layout === "chips";
  const chip = chips ? "h-7 gap-1.5 rounded-full border border-border/70 px-2.5 text-xs" : undefined;
  const nodes: ReadonlyArray<{ label: string; node: ReactNode }> = [
    {
      label: "Status",
      node: (
        <Picker
          label="Change status"
          shortcut="s"
          {...openFor("status")}
          disabled={!canManage}
          options={statusOptions}
          onPick={(value) => (value === "resolve" ? onResolve() : value === "dismiss" ? onDismiss() : value === "reopen" ? onReopen() : undefined)}
          triggerClassName={chip}
          trigger={
            <>
              <StatusIcon status={item.status} />
              <span className="truncate">{findingStatusLabel(item.status)}</span>
            </>
          }
        />
      ),
    },
    {
      label: "Severity",
      node: (
        <Tip label="Inspector sets severity when it assesses the gap">
          <span className={cn("flex h-7 items-center gap-2 px-1.5 text-[13px]", chip)}>
            <SeverityGlyph severity={item.severity} />
            {item.severity ? SEVERITY_LABEL[item.severity] : "No severity"}
          </span>
        </Tip>
      ),
    },
    {
      label: "Assignee",
      node: (
        <Picker
          label="Assign to"
          shortcut="a"
          {...openFor("assignee")}
          disabled={!canManage}
          options={assigneeOptions(people, item.assigneeId)}
          onPick={(value) => {
            const next = value === NO_ASSIGNEE ? null : value;
            const name = next ? (peopleById.get(next) ? personLabel(peopleById.get(next)!) : "them") : null;
            plan({ assigneeId: next }, name ? `Assigned to ${name}` : "Unassigned");
          }}
          triggerClassName={chip}
          trigger={
            assignee ? (
              <>
                <PersonFace person={assignee} className="size-4 text-[8px]" />
                <span className="truncate">{personLabel(assignee)}</span>
              </>
            ) : (
              <>
                <span className="size-4 rounded-full border border-dashed border-muted-foreground/50" />
                <span className="text-muted-foreground">Assign</span>
              </>
            )
          }
        />
      ),
    },
    {
      label: "Due date",
      node: (
        <Picker
          label="Set due date"
          shortcut="d"
          {...openFor("due")}
          disabled={!canManage}
          options={dueOptions(item.dueDate)}
          onPick={(value) =>
            plan({ dueDate: value === REMOVE_DUE_DATE ? null : value }, value === REMOVE_DUE_DATE ? "Due date removed" : "Due date set")
          }
          triggerClassName={chip}
          trigger={
            <>
              <CalendarIcon className={cn("size-3.5", due?.overdue ? "text-destructive" : "text-muted-foreground")} />
              <span className={due ? (due.overdue ? "text-destructive" : "") : "text-muted-foreground"}>
                {due ? due.text : chips ? "Due date" : "Set due date"}
              </span>
            </>
          }
        />
      ),
    },
  ];
  if (chips) {
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-property-chips="">
        {nodes.map((entry) => (
          <span key={entry.label} className="contents">
            {entry.node}
          </span>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {nodes.map((entry) => (
        <PropertyRow key={entry.label} label={entry.label}>
          {entry.node}
        </PropertyRow>
      ))}
    </div>
  );
}

function DocumentLink({ item, orgSegment, className }: { readonly item: FindingItem; readonly orgSegment: string; readonly className?: string }) {
  return item.policyId ? (
    <Link to="/$org/compliance/policies/$policyId" params={{ org: orgSegment, policyId: item.policyId }} hash="findings" className={className}>
      {item.documentName}
    </Link>
  ) : (
    <span className={className}>{item.documentName}</span>
  );
}

function versionLine(item: FindingItem, currentVersion: number | null): string {
  if (item.policyId === null) return "";
  const assessed = item.finding?.assessedVersion ?? null;
  return [currentVersion !== null ? `File v${currentVersion}` : null, assessed !== null ? `assessed v${assessed}` : null].filter(Boolean).join(" · ");
}

function PropertiesPanel({
  item,
  orgSegment,
  currentVersion,
  ...rest
}: Parameters<typeof PrimaryProperties>[0] & { readonly orgSegment: string; readonly currentVersion: number | null }) {
  return (
    <aside aria-label="Properties" className="w-72 shrink-0 overflow-y-auto border-l border-border/60 px-5 py-5 text-[13px]" data-properties-panel="">
      <PrimaryProperties item={item} {...rest} layout="rows" />
      <div className="mt-5 space-y-3 border-t border-border/60 pt-4">
        <div>
          <p className="mb-1 text-xs text-muted-foreground">Document</p>
          <p className="-ms-1.5 flex min-h-7 items-center gap-2 rounded-md px-1.5">
            <FileTextIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
            <DocumentLink item={item} orgSegment={orgSegment} className="truncate hover:underline" />
          </p>
          {versionLine(item, currentVersion) ? <p className="ps-5.5 text-xs text-muted-foreground">{versionLine(item, currentVersion)}</p> : null}
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">Basis</p>
          {item.basis.url ? (
            <a href={item.basis.url} target="_blank" rel="noopener noreferrer" className="-ms-1.5 flex min-h-7 items-start gap-2 rounded-md px-1.5 py-1 leading-5 transition-colors duration-150 hover:bg-foreground/[0.06]">
              <BasisIcon kind={item.basis.kind} className="mt-1 text-muted-foreground" />
              <span className="min-w-0">
                {item.basis.label}
                {/* A no-break space in a nowrap span keeps the arrow on the label's last line. */}
                <span className="whitespace-nowrap">
                  &nbsp;
                  <ArrowUpRightIcon aria-hidden className="inline size-3 align-[-1px] text-muted-foreground" />
                </span>
              </span>
            </a>
          ) : (
            <p className="flex min-h-7 items-start gap-2 py-1 leading-5">
              <BasisIcon kind={item.basis.kind} className="mt-1 text-muted-foreground" />
              {item.basis.label}
            </p>
          )}
        </div>
        <div>
          <p className="mb-1 text-xs text-muted-foreground">Raised</p>
          <p className="flex h-7 items-center gap-2">
            {item.raisedBy === "Inspector" ? <InspectorIcon className="size-3.5" /> : <ScanSearchIcon className="size-3.5 text-muted-foreground" />}
            {item.raisedBy}
            <span className="text-muted-foreground">· {relativeAge(item.raisedAt)} ago</span>
          </p>
        </div>
      </div>
    </aside>
  );
}

/** Inspector's TL;DR: problem, rule, document, next step, one line each; the full wording opens under it. */
function FindingTldr({
  item,
  orgSegment,
  currentVersion,
  canManage,
  hasFix,
}: {
  readonly item: FindingItem;
  readonly orgSegment: string;
  readonly currentVersion: number | null;
  readonly canManage: boolean;
  readonly hasFix: boolean;
}) {
  const term = "text-muted-foreground";
  return (
    <section aria-labelledby="tldr-title" className="rounded-lg border border-border/70 bg-card" data-tldr="">
      <Collapsible>
        <header className="flex h-10 items-center gap-2 pe-1.5 ps-4 text-xs text-muted-foreground">
          <InspectorIcon className="size-3.5" />
          <h2 id="tldr-title" className="font-semibold text-foreground">
            TL;DR
          </h2>
          <span>by Inspector</span>
          <CollapsibleTrigger render={<Button size="xs" variant="ghost-muted" className="group ms-auto" />} data-tldr-full="">
            Full wording
            <ChevronDownIcon aria-hidden className="transition-transform duration-150 group-data-panel-open:rotate-180" />
          </CollapsibleTrigger>
        </header>
        <dl className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 border-t border-border/70 px-4 py-3 text-[13px] leading-6 sm:grid-cols-[5rem_minmax(0,1fr)]">
          <dt className={term}>Problem</dt>
          <dd>{item.problem || item.title}</dd>
          <dt className={term}>Rule</dt>
          <dd className="[overflow-wrap:anywhere]">
            {item.basis.url ? (
              <a href={item.basis.url} target="_blank" rel="noopener noreferrer" className="underline decoration-border underline-offset-2 transition-colors duration-150 hover:decoration-foreground">
                {item.basis.label}
              </a>
            ) : (
              item.basis.label
            )}
          </dd>
          <dt className={term}>Document</dt>
          <dd className="[overflow-wrap:anywhere]">
            <DocumentLink item={item} orgSegment={orgSegment} className="underline decoration-border underline-offset-2 transition-colors duration-150 hover:decoration-foreground" />
            {versionLine(item, currentVersion) ? <span className="text-muted-foreground"> · {versionLine(item, currentVersion)}</span> : null}
          </dd>
          <dt className={term}>Next</dt>
          <dd className="font-medium" data-tldr-next="">
            {nextStep(item, { canDecide: canManage, hasFix })}
          </dd>
        </dl>
        <CollapsiblePanel>
          <div className="space-y-3 border-t border-border/70 px-4 py-3 text-[13px] leading-6">
            <p className="whitespace-pre-line text-foreground/85">{item.gap}</p>
            {item.published && item.published !== item.gap ? (
              <blockquote className="whitespace-pre-line border-s-2 border-border ps-4 text-muted-foreground">
                {item.published}
                <footer className="mt-1 flex items-center gap-1.5 text-xs">
                  <BasisIcon kind={item.basis.kind} />
                  {item.basis.label}
                </footer>
              </blockquote>
            ) : null}
          </div>
        </CollapsiblePanel>
      </Collapsible>
    </section>
  );
}

function RequiredChanges({ changes }: { readonly changes: ReadonlyArray<string> }) {
  if (changes.length === 0) return null;
  return (
    <section aria-labelledby="required-changes" className="overflow-hidden rounded-lg border border-border/70 bg-card" data-required-changes="">
      <header className="flex h-11 items-center gap-3 border-b border-border/70 px-4">
        <h2 id="required-changes" className="text-[13px] font-medium">
          Required changes
        </h2>
        <span className="text-[13px] tabular-nums text-muted-foreground">{changes.length}</span>
      </header>
      <ul className="divide-y divide-border/60">
        {changes.map((change, index) => (
          <li key={`${index}:${change}`} className="flex min-h-10 items-center gap-3 px-4">
            <span className="w-4 shrink-0 text-xs tabular-nums text-muted-foreground">{index + 1}</span>
            <Hint content={<p className="text-foreground">{change}</p>} render={<span className="min-w-0 flex-1 truncate py-2 text-[13px]" />}>
              {change}
            </Hint>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The fix's own shape while it loads (stat tiles, then a change card), so nothing jumps when it lands. */
function ProposedFixSkeleton() {
  return (
    <div role="status" aria-label="Loading the proposed fix" aria-busy="true" className="space-y-4" data-proposed-fix-state="loading">
      <div className="grid grid-cols-2 gap-2">
        {[0, 1].map((tile) => (
          <div key={tile} className="flex h-16 flex-col justify-center gap-2 rounded-lg border border-border/70 bg-card px-4">
            <Bone className="h-4 w-8" />
            <Bone className="h-3 w-20" />
          </div>
        ))}
      </div>
      <div className="overflow-hidden rounded-lg border border-border/70 bg-card">
        <div className="flex h-12 items-center gap-3 border-b border-border/70 px-4">
          <Bone className="h-3.5 w-2/5" />
          <Bone className="ms-auto h-5 w-10 rounded-full" />
        </div>
        <div className="space-y-2 px-4 py-4">
          <Bone className="h-3 w-full" />
          <Bone className="h-3 w-11/12" />
          <Bone className="h-3 w-3/5" />
        </div>
      </div>
    </div>
  );
}

/**
 * The proposed fix: every change word by word, always shown; the PDF pages
 * open per change in a dialog. Applying it is the page's primary action.
 */
function ProposedFix({
  organizationId,
  item,
  proposal,
  loading,
  unavailable,
  onRetry,
  outdated,
  people,
  editable,
}: {
  readonly organizationId: string;
  readonly item: FindingItem;
  readonly proposal: DocumentChangesView | null;
  readonly loading: boolean;
  /** The read failed: say so and offer a retry, never "no fix yet". */
  readonly unavailable: boolean;
  readonly onRetry: () => void;
  readonly outdated: boolean;
  readonly people: ReadonlyArray<OrganizationPerson>;
  /** An admin on an open finding may tick and fill the checklist. */
  readonly editable: boolean;
}) {
  // The same record the checklist reads (one shared atom), so the tile's x/y follows each tick.
  const { review } = useFixReview(organizationId, proposal);
  if (item.policyId === null) return null;
  const state = proposedFixState({ loading, unavailable, hasProposal: proposal !== null });
  if (state === "loading") return <ProposedFixSkeleton />;
  if (state === "unavailable") {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3" data-proposed-fix-state="unavailable">
        <p className="min-w-0 flex-1 text-[13px] text-foreground">The proposed fix could not be loaded.</p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          <RotateCcwIcon aria-hidden />
          Try again
        </Button>
      </div>
    );
  }
  if (state === "empty" || proposal === null) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-dashed border-border/80 px-4 py-3.5" data-proposed-fix-state="empty">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <WandSparklesIcon aria-hidden className="size-4" />
        </span>
        <div className="min-w-0 text-[13px] leading-5">
          <p className="font-medium">No proposed fix yet</p>
          <p className="text-muted-foreground">Ask Inspector in the conversation below to draft one against the current version.</p>
        </div>
      </div>
    );
  }
  return (
    <div className={cn("space-y-3", outdated && "opacity-70")}>
      <ProposedFixChanges
        key={proposal.id}
        organizationId={organizationId}
        proposal={proposal}
        checklistDone={fixChecklistProgress(proposal.outstandingChecks, review).done}
      />
      <FindingFixChecklist key={`checklist:${proposal.id}`} organizationId={organizationId} proposal={proposal} people={people} editable={editable} />
    </div>
  );
}

function DecisionCard({ item }: { readonly item: FindingItem }) {
  const decision = item.decision;
  if (!decision) return null;
  return (
    <section aria-label="Decision" className="rounded-lg border border-border/70 bg-card px-4 py-3 text-[13px]" data-decision="">
      <p className="flex items-center gap-2 font-medium">
        <StatusIcon status={decision.status} />
        {decisionSummary(decision)}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {[decision.by ?? "Unknown", decision.at ? new Date(decision.at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null]
          .filter(Boolean)
          .join(" · ")}
        {decision.status === "dismissed" && !decision.category ? " · recorded before reason categories" : ""}
      </p>
      {decision.note ? <p className="mt-2 whitespace-pre-line leading-6 text-foreground/85">{decision.note}</p> : null}
    </section>
  );
}

function MoreMenu({ item, orgSegment, canManage, onDismiss, onReopen }: { readonly item: FindingItem; readonly orgSegment: string; readonly canManage: boolean; readonly onDismiss: () => void; readonly onReopen: () => void }) {
  const navigate = useNavigate();
  return (
    <Menu>
      <Tooltip>
        <TooltipTrigger render={<MenuTrigger render={<Button size="icon-sm" variant="ghost-muted" aria-label="More actions" />} />}>
          <EllipsisIcon />
        </TooltipTrigger>
        <TooltipPopup>More</TooltipPopup>
      </Tooltip>
      <MenuPopup align="end" className="w-56">
        <MenuItem onClick={() => copyFindingLink(orgSegment, item.id)}>
          <LinkIcon />
          Copy link
        </MenuItem>
        {item.policyId ? (
          <MenuItem onClick={() => void navigate({ to: "/$org/compliance/policies/$policyId", params: { org: orgSegment, policyId: item.policyId! }, hash: "findings" })}>
            <FileTextIcon />
            Go to document
          </MenuItem>
        ) : null}
        <MenuItem onClick={focusComposer}>
          <MessageSquareIcon />
          Comment
          <MenuShortcut>C</MenuShortcut>
        </MenuItem>
        {canManage && item.status === "open" ? (
          <>
            <MenuSeparator />
            <MenuItem variant="destructive" onClick={onDismiss}>
              <CircleSlashIcon />
              Dismiss…
            </MenuItem>
          </>
        ) : null}
        {canManage && item.status !== "open" ? (
          <>
            <MenuSeparator />
            <MenuItem onClick={onReopen}>
              <RotateCcwIcon />
              Reopen…
            </MenuItem>
          </>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}

export function FindingDetailPage({
  organizationId,
  orgSegment,
  item,
  items,
  people,
  peopleById,
  personName,
  canManage,
  actions,
  fromPolicy,
  initialInspector = false,
}: FindingDetailProps) {
  const navigate = useNavigate();
  const wide = useMediaQuery("xl");
  const [openPicker, setOpenPicker] = useState<PickerKey | null>(null);
  const [resolving, setResolving] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [composerHost, setComposerHost] = useState<HTMLDivElement | null>(null);
  const dismissFlow = useDismissFlow(actions);
  const { snapshot: library } = usePolicies(organizationId);
  const policy = library?.policies.find((candidate) => candidate.id === item.policyId) ?? null;
  const currentVersion = policy ? policyVersion(policy) : null;
  const { decide } = useRecommendations(organizationId);
  const activity = usePolicyActivity(organizationId, item.policyId ?? "");
  // One timeline: the trail's events for this finding join its comments and Inspector's replies.
  const history = useMemo(
    () => findingActivityTimeline(findingActivityEvents(activity.snapshot?.activity ?? [], item, peopleById)),
    [activity.snapshot, item, peopleById],
  );
  const refreshActivity = activity.refresh;
  // The proposal is read by the changes panel too (one shared atom): a fix
  // written against an older file than the document's current one needs an update.
  const changes = useFindingChanges(organizationId, item.policyId ?? "", item.id, item.origin === "finding" ? "finding" : "recommendation");
  // A fix whose PDF was saved but whose decision failed is the document's
  // current version, not an outdated one: Apply retries the decision with it.
  const [savedFix, setSavedFix] = useState<{ readonly proposalId: string; readonly version: number } | null>(null);
  const savedVersion = changes.proposal !== null && savedFix?.proposalId === changes.proposal.id ? savedFix.version : null;
  const fixOutdated =
    item.status === "open" &&
    changes.proposal !== null &&
    currentVersion !== null &&
    changes.proposal.sourceVersion < currentVersion &&
    savedVersion !== currentVersion;

  // Prev / next follow the queue as it was last arranged (its stage and filters).
  const ordered = useMemo(() => {
    const view = savedFindingQueueView(organizationId);
    return filterFindingItems(items, view, personName)
      .filter((candidate) => matchesFindingStage(candidate, view.stage) || candidate.id === item.id)
      .sort(compareFindingItems(view.ordering));
  }, [items, organizationId, personName, item.id]);
  const index = ordered.findIndex((candidate) => candidate.id === item.id);
  const previous = index > 0 ? ordered[index - 1] : undefined;
  const next = index >= 0 ? ordered[index + 1] : undefined;
  const nextOpen = item.status === "open" ? null : nextOpenFindingItem(ordered, item.id);

  const back = fromPolicy && fromPolicy === item.policyId;
  const goBack = () =>
    back
      ? void navigate({ to: "/$org/compliance/policies/$policyId", params: { org: orgSegment, policyId: fromPolicy }, hash: "findings" })
      : void navigate({ to: "/$org/compliance/findings", params: { org: orgSegment } });
  const open = (id: string) => void navigate({ to: "/$org/compliance/findings/$findingId", params: { org: orgSegment, findingId: id } });

  useEffect(() => {
    const keys: Record<string, PickerKey> = { s: "status", a: "assignee", d: "due" };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && target.closest("input, textarea, [contenteditable], [role='dialog'], [data-slot$='popup'], [role='menu']") !== null) return;
      if (event.key === "j" && next) open(next.id);
      else if (event.key === "k" && previous) open(previous.id);
      else if (event.key === "Escape") goBack();
      else if (event.key === "c") {
        event.preventDefault();
        focusComposer();
      } else if (keys[event.key] && canManage) {
        event.preventDefault();
        setOpenPicker(keys[event.key]!);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const settle = (error: string | null, success: string) => {
    refreshActivity();
    toastManager.add(error ? { type: "error", title: "Could not save", description: error } : { type: "success", title: success });
    return error;
  };
  const startDismiss = () => dismissFlow.open([item]);
  const startReopen = () => setReopening(true);
  const startResolve = () => setResolving(true);
  const isOpen = item.status === "open";
  // After a reopen the Reopen button that had focus is gone with the decision
  // (ReopenDialog leaves focus to the caller), so focus moves to the decision
  // that replaces it once the finding reads as open.
  const focusDecisionWhenOpen = useRef(false);
  useEffect(() => {
    if (!isOpen || !focusDecisionWhenOpen.current) return;
    focusDecisionWhenOpen.current = false;
    const frame = window.requestAnimationFrame(() =>
      window.document
        .querySelector<HTMLElement>(`[data-finding-view="${CSS.escape(item.id)}"] > header :is([data-header-apply], [data-header-resolve])`)
        ?.focus(),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, item.id]);
  // Apply fix: Inspector's proposed PDF becomes the next version and resolves the item with it.
  const applyProposal = useApplyProposal(organizationId, item.policyId ?? "");
  const [applying, setApplying] = useState(false);
  const proposal = changes.proposal;
  // "Review changes" links (an Inspector run) open with ?tab=changes: once the
  // fix has loaded, the page scrolls to it, once per finding.
  const wantsChanges = useSearch({ strict: false, select: (search) => (search as { tab?: unknown }).tab === "changes" });
  const scrolledToChanges = useRef<string | null>(null);
  useEffect(() => {
    if (!wantsChanges || changes.loading || scrolledToChanges.current === item.id) return;
    scrolledToChanges.current = item.id;
    const frame = window.requestAnimationFrame(() =>
      window.document
        .querySelector<HTMLElement>(`[data-finding-view="${CSS.escape(item.id)}"] [data-proposed-fix]`)
        ?.scrollIntoView({ block: "start" }),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [changes.loading, item.id, wantsChanges]);
  const canApply =
    canManage &&
    isOpen &&
    proposal !== null &&
    !fixOutdated &&
    currentVersion !== null &&
    (item.finding !== null || item.suggestion?.kind === "control_amendment");
  const applyFix = async (): Promise<boolean> => {
    if (!proposal || applying) return false;
    setApplying(true);
    try {
      const resolvedVersion = await applyProposal(proposal, currentVersion);
      setSavedFix({ proposalId: proposal.id, version: resolvedVersion });
      const note = proposalDecisionNote(proposal, null);
      const error = item.finding
        ? await actions.resolveFinding(item, { resolvedVersion, note })
        : await decide(item.id, "apply", { note, resolvedVersion });
      return settle(error, `Fix applied · saved as File v${resolvedVersion}`) === null;
    } catch (cause) {
      toastManager.add({ type: "error", title: "Could not apply the fix", description: cause instanceof Error ? cause.message : "Try again." });
      return false;
    } finally {
      setApplying(false);
    }
  };
  // The resolve form's evidence: a finding records its version, a suggestion is applied with it.
  const resolveWith = async (evidence: ResolutionEvidence) => {
    const error = item.finding
      ? await actions.resolveFinding(item, {
          ...(evidence.resolvedVersion === undefined ? {} : { resolvedVersion: evidence.resolvedVersion }),
          note: evidence.note,
        })
      : await decide(item.id, "apply", {
          note: evidence.note,
          ...(evidence.resolvedVersion === undefined ? {} : { resolvedVersion: evidence.resolvedVersion }),
          ...(evidence.resolvedPolicyId === undefined ? {} : { resolvedPolicyId: evidence.resolvedPolicyId }),
        });
    if (error === null) setResolving(false);
    return settle(error, item.finding && evidence.resolvedVersion ? `Resolved by File v${evidence.resolvedVersion}` : "Resolved");
  };
  const pickers = {
    item,
    people,
    peopleById,
    canManage,
    actions,
    onResolve: startResolve,
    onDismiss: startDismiss,
    onReopen: startReopen,
    openPicker,
    setOpenPicker,
  };

  return (
    <div data-regulation-page="true" className="relative flex min-h-0 flex-1 flex-col" data-finding-view={item.id}>
      <header className={cn("flex h-11 shrink-0 items-center gap-2 border-b border-border/60 pe-2 ps-2 text-[13px] sm:pe-3 sm:ps-3", COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS)}>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button size="icon-sm" variant="ghost-muted" onClick={goBack} aria-label={back ? "Back to the document" : "All findings"} />
            }
          >
            <ArrowLeftIcon aria-hidden />
          </TooltipTrigger>
          <TooltipPopup>
            {back ? "Back to the document" : "All findings"} <Kbd className="ms-1">Esc</Kbd>
          </TooltipPopup>
        </Tooltip>
        <Link to="/$org/compliance/findings" params={{ org: orgSegment }} className="text-muted-foreground transition-colors duration-150 hover:text-foreground max-sm:hidden">
          Findings
        </Link>
        <ChevronRightIcon aria-hidden className="size-3.5 text-muted-foreground/60 max-sm:hidden" />
        <StatusIcon status={item.status} />
        <span className="min-w-0 truncate font-medium max-md:hidden">{item.title}</span>
        <MoreMenu item={item} orgSegment={orgSegment} canManage={canManage} onDismiss={startDismiss} onReopen={startReopen} />
        <div className="ms-auto flex shrink-0 items-center gap-2">
          <FindingDecisionControl
            organizationId={organizationId}
            item={item}
            canManage={canManage}
            currentVersion={currentVersion}
            apply={{ available: canApply, changes: proposal?.edits.length ?? 0, savedVersion, applying, onApply: applyFix }}
            resolving={resolving}
            onResolvingChange={setResolving}
            onResolve={resolveWith}
            onReopen={startReopen}
          />
          {nextOpen ? (
            <Button size="sm" variant="outline" onClick={() => open(nextOpen.id)} aria-label="Next open finding">
              <span className="max-sm:hidden">Next open</span>
              <ArrowRightIcon aria-hidden />
            </Button>
          ) : null}
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
            <div className="mx-auto max-w-[60rem] space-y-6 px-4 pb-48 pt-5 sm:px-8 sm:pt-8">
              <div className="space-y-4">
                <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{item.title}</h1>
                {wide ? null : <PrimaryProperties {...pickers} layout="chips" />}
                <FindingTldr item={item} orgSegment={orgSegment} currentVersion={currentVersion} canManage={canManage} hasFix={changes.proposal !== null && !fixOutdated} />
              </div>
              {item.chip?.kind === "needs-recheck" ? (
                <Alert variant="warning" role="status" controlAlignment="first-line" data-recheck-banner="">
                  <HistoryIcon />
                  <AlertTitle>Needs re-check</AlertTitle>
                  <AlertDescription>
                    {item.chip.detail}. The required changes below describe the document as it was then.
                    {item.finding && findingCanBeRechecked(item.finding) ? " Ask Inspector to re-check it in the conversation below." : ""}
                  </AlertDescription>
                </Alert>
              ) : null}
              <RequiredChanges changes={item.requiredChanges} />
              {isOpen && !canManage ? <WaitingForAdmin /> : null}
              <DecisionCard item={item} />
              {item.policyId ? (
                <section aria-labelledby="proposed-fix-title" className="space-y-3" data-proposed-fix="">
                  <h2 id="proposed-fix-title" className="flex items-center gap-2 text-[13px] font-semibold">
                    <WandSparklesIcon aria-hidden className="size-3.5 text-muted-foreground" />
                    Proposed fix
                    {changes.proposal ? (
                      <span className="rounded-full border border-border/70 px-1.5 py-px text-[11px] font-normal text-muted-foreground">
                        for File v{changes.proposal.sourceVersion}
                      </span>
                    ) : null}
                    {fixOutdated ? (
                      <span
                        data-state-chip="fix-outdated"
                        title={`Written against File v${changes.proposal!.sourceVersion}; the document is now File v${currentVersion}. Ask Inspector to update it.`}
                        className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:text-amber-200"
                      >
                        Fix needs update
                      </span>
                    ) : null}
                  </h2>
                  <ProposedFix
                    organizationId={organizationId}
                    item={item}
                    proposal={changes.proposal}
                    loading={changes.loading}
                    unavailable={changes.unavailable}
                    onRetry={changes.refresh}
                    outdated={fixOutdated}
                    people={people}
                    editable={canManage && isOpen}
                  />
                </section>
              ) : null}
              {item.finding ? (
                <FindingConversation
                  key={`conversation:${organizationId}:${item.id}`}
                  organizationId={organizationId}
                  findingId={item.id}
                  orgSegment={orgSegment}
                  initialInspector={initialInspector}
                  canAskAI={isOpen}
                  recheck={item.chip?.kind === "needs-recheck" && findingCanBeRechecked(item.finding)}
                  composerHost={composerHost}
                  composerLayout="tabs"
                  history={history}
                />
              ) : item.policyId ? (
                // A suggestion about a document is asked of Inspector like a finding is.
                <FindingConversation
                  key={`conversation:${organizationId}:${item.id}`}
                  organizationId={organizationId}
                  findingId={item.id}
                  orgSegment={orgSegment}
                  initialInspector={initialInspector}
                  canAskAI={isOpen}
                  composerHost={composerHost}
                  composerLayout="tabs"
                  history={history}
                  subject="suggestion"
                />
              ) : (
                <SuggestionConversation organizationId={organizationId} recommendationId={item.id} composerHost={composerHost} composerLayout="tabs" history={history} />
              )}
            </div>
          </div>
          {/* The composer rests narrow as one line and widens once you write. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 px-3 pb-3 sm:px-8" data-floating-composer="">
            <div
              ref={setComposerHost}
              className="pointer-events-auto mx-auto w-full max-w-2xl transition-[max-width] duration-[280ms] ease-[cubic-bezier(0.32,0.72,0,1)] has-[[data-review-composer-resting=true]]:max-w-lg motion-reduce:transition-none"
            />
          </div>
        </div>
        {wide ? <PropertiesPanel {...pickers} orgSegment={orgSegment} currentVersion={currentVersion} layout="rows" /> : null}
      </div>
      <ReopenDialog
        open={reopening && !isOpen}
        onOpenChange={setReopening}
        title={item.title}
        summary={isOpen ? null : item.finding ? findingReopenSummary(item.finding) : item.suggestion ? suggestionReopenSummary(item.suggestion) : null}
        onReopen={async () => {
          focusDecisionWhenOpen.current = true;
          const error = await actions.reopen(item);
          if (error !== null) focusDecisionWhenOpen.current = false;
          return settle(error, "Finding reopened · the earlier decision stays in its history");
        }}
      />
      {dismissFlow.dialog}
    </div>
  );
}
